import { ENGINE_TYPES, LOT_TYPES } from './ingest';
/**
 * Smart lot search — the pure half (no I/O).
 *
 * One normalisation (`normalizeSearchText`) is applied identically to the lot
 * document, to the dictionary aliases (at module load) and to the query, so a
 * query word and a document word can only ever compare equal or not — never
 * "almost". `buildSearchDoc` derives the denormalised `search_text` /
 * `search_tags` / `search_v` fields stored on `alpicar_cars`;
 * `tokenizeSearchQuery` turns the user's chips into the AND-ed tokens the cars
 * query builder matches against them.
 *
 * Changing the dictionary or the document recipe means bumping
 * `SEARCH_DOC_VERSION` and re-running the backfill (`search_v < N`).
 */
// ── Normalisation ─────────────────────────────────────────────────────────
/**
 * Latin diacritics folded by an explicit map — NOT NFD + strip-marks, which
 * would also fold the Cyrillic `ї` (U+0457) and `й` (U+0439). The `i` row is
 * the LATIN î ï ì í (U+00EE/EF/EC/ED) only — never the Cyrillic look-alikes.
 */
const CHAR_FOLDS = {
    ё: 'е',
    ä: 'a',
    à: 'a',
    á: 'a',
    â: 'a',
    ã: 'a',
    å: 'a',
    ö: 'o',
    ò: 'o',
    ó: 'o',
    ô: 'o',
    õ: 'o',
    ü: 'u',
    ù: 'u',
    ú: 'u',
    û: 'u',
    é: 'e',
    è: 'e',
    ê: 'e',
    ë: 'e',
    ç: 'c',
    ñ: 'n',
    ß: 'ss',
    // Central-European Latin (Škoda, Łódź, Dvořák…) — Latin code points only.
    š: 's',
    č: 'c',
    ž: 'z',
    ł: 'l',
    ř: 'r',
    ś: 's',
    ć: 'c',
    ń: 'n',
    ź: 'z',
    ż: 'z',
    ą: 'a',
    ę: 'e',
    ő: 'o',
    ű: 'u',
    'î': 'i',
    'ï': 'i',
    'ì': 'i',
    'í': 'i',
};
const CHAR_FOLD_RE = new RegExp(`[${Object.keys(CHAR_FOLDS).join('')}]`, 'gu');
/** ' ’ ʼ ` — removed outright, so `мʼякий` and `м'який` both become `мякий`. */
const APOSTROPHE_RE = new RegExp("['’ʼ`]", 'gu');
/*
 * NO LOOKBEHIND in this file: it is re-exported from the contracts barrel that
 * 'use client' code imports, and Safari < 16.4 (iOS 15) throws a SyntaxError
 * on a lookbehind assertion at module load (search.spec.ts guards this). The
 * left neighbour is captured and written back instead; only the RIGHT
 * neighbour is a lookahead, so it stays unconsumed and `1х2х3` / `1,2,3`
 * still convert every separator.
 */
/** Cyrillic `х` (U+0445) between digits is a typed `x`: `4х4 → 4x4`. */
const CYRILLIC_X_RE = new RegExp(String.raw `(\d)х(?=\d)`, 'gu');
/** European decimal comma: `1,6 → 1.6`. */
const DECIMAL_COMMA_RE = new RegExp(String.raw `(\d),(?=\d)`, 'gu');
/**
 * Everything that is not a letter or a number becomes a space, except the
 * captured cases (kept together with their left neighbour): a `.` between two
 * digits or between a digit and a letter (`1.4tsi`), and a `+` right after a
 * letter or digit (`450h+`).
 */
const PUNCTUATION_RE = new RegExp(String.raw `(\d\.(?=[\p{L}\p{N}])|\p{L}\.(?=\d)|[\p{L}\p{N}]\+)|[^\p{L}\p{N}]`, 'gu');
const WHITESPACE_RE = /\s+/g;
/** Deterministic search normalisation (spec §4) — same for documents, aliases and queries. */
export function normalizeSearchText(input) {
    return input
        .normalize('NFKC')
        .toLowerCase()
        .replace(APOSTROPHE_RE, '')
        .replace(CHAR_FOLD_RE, (ch) => CHAR_FOLDS[ch] ?? ch)
        .replace(CYRILLIC_X_RE, '$1x')
        .replace(DECIMAL_COMMA_RE, '$1.')
        .replace(PUNCTUATION_RE, (_match, kept) => kept ?? ' ')
        .replace(WHITESPACE_RE, ' ')
        .trim();
}
// ── Dictionary ────────────────────────────────────────────────────────────
export const SEARCH_CATEGORIES = [
    'lot',
    'fuel',
    'gear',
    'drive',
    'body',
    'color',
    'brand',
];
/** Which doc fields tag each category (spec §5). `{en, ua}` fields expand to both languages. */
const CATEGORY_DOC_FIELDS = {
    // `important_information` is prose: only the anchored two-wheeler label below reads it.
    lot: ['type', 'important_information'],
    fuel: ['fuel_type', 'name', 'model'],
    gear: ['transmission'],
    drive: ['drive', 'name', 'model'],
    body: ['type', 'name'],
    color: ['color'],
    brand: ['brand', 'make', 'name'],
};
function group(category, slug, aliases, docPattern, extra = {}) {
    return {
        tag: `${category}:${slug}`,
        category,
        aliases,
        docPatterns: docPattern === null ? [] : [docPattern],
        ...extra,
    };
}
/** `name` / `model` only carry explicit drive markers — `front`/`heck`/`rear` would hit "Frontera". */
const AWD_MODEL_PATTERN = '4x4|4wd|awd|quattro|4motion|xdrive|4matic|allrad';
const DRIVE_FIELD_ONLY = {
    name: [],
    model: [],
};
/**
 * Lot groups tag from `type` only; `important_information` (free prose) is
 * read solely through the alpicar two-wheeler label below.
 */
const LOT_TYPE_FIELD_ONLY = {
    important_information: [],
};
/**
 * alpicar's structured two-wheeler category, as the bot carries it inside
 * `important_information`: «Категорія двоколісних: Велосипед», glued
 * («двоколіснихВелосипед») or «Категорія двоколісних транспортних засобів: …».
 * Needed because the bot sometimes sends such a bike as lot_type `other`
 * (MOMAS Eywa+Glacier, 2026-10), which left it out of the `lot:bicycle` tag —
 * so «велосипед» found it through the literal word while «вело» did not.
 */
const TWO_WHEELER_LABEL = 'двоколісних ?(?:транспортних засобів )?';
/**
 * The search dictionary — every row of spec §5. Note: docPatterns run over
 * normalised text, where a `-` is already a space, so the spec's `e-?bike`,
 * `plug-?in` and `pick-?up` are written `[ -]?` to keep matching `e bike`.
 */
export const SEARCH_SYNONYM_GROUPS = [
    // ── lot (also tagged straight from the `lot_type` enum) ──
    group('lot', 'car', ['авто', 'автомобіль', 'легковик', 'легковий', 'легкове', 'машина', 'car', 'pkw', 'personenwagen'], null),
    group('lot', 'motorcycle', ['мотоцикл', 'мото', 'байк', 'скутер', 'мопед', 'motorcycle', 'motorbike', 'moto', 'motorrad', 'scooter'], 'motorrad|motorcycle|мотоцикл', { docPatternsByField: LOT_TYPE_FIELD_ONLY }),
    group('lot', 'bicycle', ['велосипед', 'велосипеди', 'вело', 'велик', 'bicycle', 'bike', 'fahrrad', 'velo'], 'fahrrad|bicycle|велосипед', {
        alsoMatches: ['lot:ebike'],
        docPatternsByField: { important_information: [`${TWO_WHEELER_LABEL}велосипед`] },
    }),
    group('lot', 'ebike', ['електровелосипед', 'електровелик', 'ебайк', 'e bike', 'ebike', 'pedelec'], 'e[ -]?bike|pedelec|електровелосипед', { docPatternsByField: LOT_TYPE_FIELD_ONLY }),
    group('lot', 'trailer', ['причіп', 'прицеп', 'trailer', 'anhanger'], 'anh[aä]nger|trailer|причіп', {
        docPatternsByField: LOT_TYPE_FIELD_ONLY,
    }),
    group('lot', 'caravan', [
        'кемпер',
        'автобудинок',
        'будинок на колесах',
        'караван',
        'житловий причіп',
        'caravan',
        'camper',
        'wohnmobil',
        'wohnwagen',
        'motorhome',
    ], 'wohnmobil|wohnwagen|caravan|camper|кемпер', { docPatternsByField: LOT_TYPE_FIELD_ONLY }),
    group('lot', 'truck', ['вантажівка', 'вантажний', 'грузовик', 'truck', 'lkw'], 'lkw|truck|вантаж', {
        docPatternsByField: LOT_TYPE_FIELD_ONLY,
    }),
    group('lot', 'van', ['фургон', 'мікроавтобус', 'бус', 'van', 'minibus', 'transporter', 'lieferwagen'], 'lieferwagen|transporter|minibus|фургон|мікроавтобус', { docPatternsByField: LOT_TYPE_FIELD_ONLY }),
    // ── fuel (also tagged straight from the `fuel_kind` enum) ──
    group('fuel', 'petrol', [
        'бензин',
        'бензиновий',
        'бензинова',
        'бенз',
        'petrol',
        'gasoline',
        'benzin',
        'benzine',
        'essence',
        // Engine codes (TSI, TFSI) are not query aliases for the same reason as the diesel codes below.
    ], String.raw `benzin|petrol|gasoline|essence|бензин|\d?(t?fsi|tsi)\b`),
    group('fuel', 'diesel', [
        'дизель',
        'дізель',
        'дизельний',
        'дизельна',
        'дизельне',
        'дт',
        'солярка',
        'diesel',
        // Engine codes (TDI, CDI, dCi…) are deliberately NOT query aliases: a buyer typing "1,6 TDI" wants a TDI,
        // not every 1.6 diesel. They still tag the lot through the doc pattern below and match literally as words.
    ], String.raw `diesel|дизел|\d?(tdi|cdi|crdi|hdi|dci|tdci|d4d|jtd|multijet|bluehdi|bluetec)\b`),
    group('fuel', 'electric', ['електро', 'електричний', 'електромобіль', 'електрокар', 'ev', 'bev', 'electric', 'elektro'], 'elektro|electric|електр'),
    group('fuel', 'hybrid', ['гібрид', 'гібридний', 'hybrid', 'hev'], 'hybrid|гібрид', {
        alsoMatches: ['fuel:mhev', 'fuel:phev'],
    }),
    group('fuel', 'mhev', ['мʼякий гібрид', 'mild hybrid', 'mhev', '48v'], 'mild|mhev|48 ?v'),
    group('fuel', 'phev', ['плагін гібрид', 'підзаряджуваний гібрид', 'phev', 'plug in', 'plugin'], 'plug[ -]?in|phev'),
    group('fuel', 'lpg', ['газ', 'гбо', 'пропан', 'lpg', 'autogas'], 'lpg|autogas|пропан|гбо'),
    group('fuel', 'cng', ['метан', 'cng', 'erdgas'], 'cng|erdgas|метан'),
    group('fuel', 'hydrogen', ['водень', 'водневий', 'hydrogen', 'fcev', 'wasserstoff'], 'hydrogen|wasserstoff|водн'),
    // ── gear ──
    group('gear', 'manual', [
        'механіка',
        'механічна',
        'механічна коробка',
        'ручна',
        'ручна коробка',
        'мкпп',
        'мех',
        'manual',
        'manuell',
        'schaltgetriebe',
    ], 'manu|schalt|механ|ручн'),
    group('gear', 'automatic', [
        'автомат',
        'автоматична',
        'автоматична коробка',
        'акпп',
        'робот',
        'варіатор',
        'dsg',
        'cvt',
        'tiptronic',
        'steptronic',
        'automatic',
        'automatik',
        'automatique',
    ], 'autom|автомат|dsg|cvt|tiptronic|steptronic|варіатор|робот'),
    // ── drive ──
    group('drive', 'awd', ['повний привід', 'повний', '4x4', '4wd', 'awd', 'quattro', '4motion', 'xdrive', '4matic', 'allrad'], `${AWD_MODEL_PATTERN}|повн`, { docPatternsByField: { name: [AWD_MODEL_PATTERN], model: [AWD_MODEL_PATTERN] } }),
    group('drive', 'fwd', ['передній привід', 'передній', 'fwd', 'frontantrieb'], 'front|fwd|передн', {
        docPatternsByField: DRIVE_FIELD_ONLY,
    }),
    group('drive', 'rwd', ['задній привід', 'задній', 'rwd', 'heckantrieb'], 'heck|rwd|rear|задн', {
        docPatternsByField: DRIVE_FIELD_ONLY,
    }),
    // ── body ──
    group('body', 'sedan', ['седан', 'sedan', 'limousine'], 'limousine|sedan|седан'),
    group('body', 'wagon', ['універсал', 'комбі', 'kombi', 'wagon', 'estate', 'touring', 'avant', 'variant'], 
    // `\bavant\b`: a bare `avant` would also hit the Mercedes "Avantgarde" trim.
    String.raw `kombi|wagon|estate|touring|\bavant\b|variant|універсал|комбі`),
    group('body', 'suv', ['позашляховик', 'кросовер', 'джип', 'suv', 'crossover', 'gelandewagen'], 'suv|gel[aä]nde|crossover|позашлях|кросовер|джип'),
    group('body', 'hatchback', ['хетчбек', 'хечбек', 'hatchback'], 'hatch|schr[aä]gheck|хетчбек'),
    group('body', 'coupe', ['купе', 'coupe'], 'coup|купе'),
    group('body', 'cabrio', ['кабріолет', 'кабрік', 'cabrio', 'cabriolet', 'convertible', 'roadster', 'родстер'], 'cabrio|convertible|roadster|кабріолет|родстер'),
    group('body', 'minivan', ['мінівен', 'minivan', 'grossraumlimousine'], 'minivan|grossraum|мінівен'),
    group('body', 'pickup', ['пікап', 'pickup', 'pick up'], 'pick[ -]?up|пікап'),
    // ── color (`interior_color` is NOT a doc field) ──
    group('color', 'black', ['чорний', 'чорна', 'чорне', 'black', 'schwarz'], 'schwarz|black|чорн'),
    group('color', 'white', ['білий', 'біла', 'біле', 'white', 'weiss'], 'weiss|white|біл'),
    group('color', 'silver', ['сріблястий', 'срібляста', 'срібний', 'срібло', 'silver', 'silber'], 'silber|silver|срібл'),
    group('color', 'grey', ['сірий', 'сіра', 'сіре', 'grey', 'gray', 'grau'], 'grau|grey|gray|сір'),
    group('color', 'blue', ['синій', 'синя', 'синє', 'блакитний', 'blue', 'blau'], 'blau|blue|син|блакит'),
    group('color', 'red', ['червоний', 'червона', 'червоне', 'red', 'rot'], String.raw `\brot|\bred\b|червон`),
    group('color', 'green', ['зелений', 'зелена', 'зелене', 'green', 'grun'], 'gr[uü]n|green|зелен'),
    group('color', 'brown', ['коричневий', 'коричнева', 'brown', 'braun'], 'braun|brown|коричн'),
    group('color', 'beige', ['бежевий', 'бежева', 'beige'], 'beige|беж'),
    group('color', 'yellow', ['жовтий', 'жовта', 'yellow', 'gelb'], 'gelb|yellow|жовт'),
    group('color', 'orange', ['помаранчевий', 'оранжевий', 'orange'], 'orange|помаранч|оранж'),
    // ── brand ──
    group('brand', 'mercedes', ['мерс', 'мерседес', 'мерседес бенц', 'mercedes', 'benz', 'mb'], String.raw `mercedes|\bbenz\b`),
    group('brand', 'bmw', ['бмв', 'бумер', 'бімер', 'bmw'], String.raw `\bbmw\b`),
    group('brand', 'volkswagen', ['фольксваген', 'фольц', 'volkswagen', 'vw'], String.raw `volkswagen|\bvw\b`),
    group('brand', 'audi', ['ауді', 'ауди', 'audi'], String.raw `\baudi\b`),
    group('brand', 'skoda', ['шкода', 'skoda'], 'skoda'),
    group('brand', 'toyota', ['тойота', 'toyota'], 'toyota'),
    group('brand', 'opel', ['опель', 'opel'], String.raw `\bopel\b`),
    group('brand', 'renault', ['рено', 'renault'], 'renault'),
    group('brand', 'peugeot', ['пежо', 'peugeot'], 'peugeot'),
    group('brand', 'citroen', ['сітроен', 'ситроен', 'citroen'], 'citroen'),
    group('brand', 'ford', ['форд', 'ford'], String.raw `\bford\b`),
    group('brand', 'fiat', ['фіат', 'fiat'], String.raw `\bfiat\b`),
    group('brand', 'volvo', ['вольво', 'volvo'], 'volvo'),
    group('brand', 'nissan', ['ніссан', 'нісан', 'nissan'], 'nissan'),
    group('brand', 'honda', ['хонда', 'honda'], 'honda'),
    group('brand', 'mazda', ['мазда', 'mazda'], 'mazda'),
    group('brand', 'hyundai', ['хюндай', 'хендай', 'хундай', 'hyundai'], 'hyundai'),
    group('brand', 'kia', ['кіа', 'kia'], String.raw `\bkia\b`),
    group('brand', 'porsche', ['порше', 'porsche'], 'porsche'),
    group('brand', 'tesla', ['тесла', 'tesla'], 'tesla'),
    group('brand', 'mini', ['міні', 'mini'], String.raw `\bmini\b`),
    group('brand', 'seat', ['сеат', 'seat'], String.raw `\bseat\b`),
    group('brand', 'land-rover', ['ленд ровер', 'лендровер', 'land rover', 'landrover', 'range rover'], 'land ?rover|range ?rover'),
    group('brand', 'subaru', ['субару', 'subaru'], 'subaru'),
    group('brand', 'suzuki', ['сузукі', 'suzuki'], 'suzuki'),
    group('brand', 'mitsubishi', ['міцубісі', 'мітсубісі', 'mitsubishi'], 'mitsubishi'),
    group('brand', 'lexus', ['лексус', 'lexus'], 'lexus'),
    group('brand', 'dacia', ['дачія', 'dacia'], 'dacia'),
    group('brand', 'alfa-romeo', ['альфа ромео', 'alfa romeo', 'alfa'], 'alfa ?romeo'),
    group('brand', 'jaguar', ['ягуар', 'jaguar'], 'jaguar'),
    group('brand', 'chevrolet', ['шевроле', 'chevrolet'], 'chevrolet'),
];
/**
 * Build the alias lookup. Throws when one normalised alias belongs to two
 * groups — a query word must resolve to exactly one tag. Exported for tests.
 */
export function buildSearchAliasIndex(groups) {
    const entries = groups.flatMap((g) => g.aliases
        .map(normalizeSearchText)
        .filter(Boolean)
        .map((alias) => [alias, g]));
    const byAlias = entries.reduce((map, [alias, g]) => {
        const owner = map.get(alias);
        if (owner && owner !== g) {
            throw new Error(`Duplicate search alias: "${alias}" (${owner.tag}, ${g.tag})`);
        }
        return map.set(alias, g);
    }, new Map());
    const multiWord = [...byAlias.keys()]
        .filter((alias) => alias.includes(' '))
        .sort((a, b) => b.length - a.length)
        .map((alias) => alias.split(' '));
    return { byAlias, multiWord };
}
const ALIAS_INDEX = buildSearchAliasIndex(SEARCH_SYNONYM_GROUPS);
/** Dictionary group for an already-normalised token, if any. */
export function findSearchSynonymGroup(normalizedToken) {
    return ALIAS_INDEX.byAlias.get(normalizedToken);
}
/** A heavier query is cut here — each token is one more AND-ed regex. */
export const MAX_SEARCH_QUERY_TOKENS = 8;
const STOP_WORDS = new Set([
    'і',
    'й',
    'та',
    'з',
    'із',
    'зі',
    'на',
    'в',
    'у',
    'до',
    'для',
    'по',
    'від',
    'або',
    'a',
    'an',
    'the',
    'and',
    'with',
    'for',
    'of',
    'in',
    'or',
]);
const EDGE_PUNCTUATION_RE = new RegExp(String.raw `^[^\p{L}\p{N}]+|[^\p{L}\p{N}+]+$`, 'gu');
const SINGLE_DIGIT_RE = /^\d$/;
function chipPieces(chip) {
    return chip.split(WHITESPACE_RE).flatMap((rawWord) => {
        const norms = normalizeSearchText(rawWord).split(' ').filter(Boolean);
        const whole = rawWord.replace(EDGE_PUNCTUATION_RE, '');
        return norms.map((norm) => ({ norm, raw: norms.length === 1 && whole ? whole : norm }));
    });
}
/** Word count of the longest multi-word alias starting at `start`, or 1. */
function aliasSpanAt(pieces, start) {
    const hit = ALIAS_INDEX.multiWord.find((words) => words.every((word, offset) => pieces[start + offset]?.norm === word));
    return hit?.length ?? 1;
}
function chipTokens(chip) {
    const pieces = chipPieces(chip);
    const tokens = [];
    for (let start = 0; start < pieces.length;) {
        const span = pieces.slice(start, start + aliasSpanAt(pieces, start));
        tokens.push({
            norm: span.map((p) => p.norm).join(' '),
            raw: span.map((p) => p.raw).join(' '),
        });
        start += span.length;
    }
    return tokens;
}
function isMeaningfulToken({ norm }) {
    if (STOP_WORDS.has(norm))
        return false;
    return [...norm].length > 1 || SINGLE_DIGIT_RE.test(norm);
}
/**
 * Chips → AND-ed query tokens (spec §4): multi-word dictionary aliases are
 * pulled out greedily (longest first) as one token, stop words and one-letter
 * tokens are dropped (a single digit is kept), duplicates collapse by `norm`,
 * and at most MAX_SEARCH_QUERY_TOKENS survive.
 */
export function tokenizeSearchQuery(chips) {
    const seen = new Set();
    const tokens = [];
    for (const chip of chips) {
        for (const token of chipTokens(chip)) {
            if (!isMeaningfulToken(token) || seen.has(token.norm))
                continue;
            seen.add(token.norm);
            tokens.push(token);
            if (tokens.length === MAX_SEARCH_QUERY_TOKENS)
                return tokens;
        }
    }
    return tokens;
}
/**
 * Escape every regex metacharacter so user input is matched literally (and
 * cannot inject a pattern). Same character class as `escapeRegex` in
 * apps/api/src/modules/cars/utils/escape-regex.ts and the auction's
 * `@shared/utils/escapeRegex` — contracts cannot import either.
 */
function escapeSearchRegex(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/** 1–4 digits: a year or an engine figure, matched as a whole number (spec §7). */
const SHORT_NUMBER_TOKEN_RE = /^\d{1,4}$/;
/**
 * A Cyrillic word matches from the START of a word only. The Ukrainian lot
 * text is prose (damage notes, `important_information`), so a substring hit is
 * usually an unrelated word: "вело" used to find «призвело», «велосипедистів»
 * (a BMW's assistance list), «велопідставка». `search_text` is normalised —
 * every non-letter is already a single space — so a space or the string start
 * is the word boundary; no `\b`, which MongoDB applies to ASCII only.
 */
const CYRILLIC_TOKEN_RE = /\p{Script=Cyrillic}/u;
/**
 * The smart-search condition for one token against `search_text` /
 * `search_tags` (both normalised, so no `$options: 'i'`):
 *  - a dictionary word matches its tag (plus `alsoMatches`) or the whole word;
 *  - a short number matches only as a whole number — `2015` must not hit the
 *    mileage `120150`, and the leading `[^0-9.]` keeps it off `1.2015`;
 *  - a Cyrillic word matches from the start of a word (see CYRILLIC_TOKEN_RE);
 *  - anything else is a substring (VIN fragments, model codes).
 */
function smartSearchCondition({ norm }) {
    const synonymGroup = findSearchSynonymGroup(norm);
    if (synonymGroup) {
        return {
            $or: [
                { search_tags: { $in: [synonymGroup.tag, ...(synonymGroup.alsoMatches ?? [])] } },
                { search_text: { $regex: `(?:^| )${escapeSearchRegex(norm)}(?: |$)` } },
            ],
        };
    }
    if (SHORT_NUMBER_TOKEN_RE.test(norm)) {
        return { search_text: { $regex: `(?:^|[^0-9.])${norm}(?:[^0-9]|$)` } };
    }
    if (CYRILLIC_TOKEN_RE.test(norm)) {
        return { search_text: { $regex: `(?:^| )${escapeSearchRegex(norm)}` } };
    }
    return { search_text: { $regex: escapeSearchRegex(norm) } };
}
/**
 * Chips → one MongoDB condition per AND-ed query token (spec §7). THE single
 * implementation behind both the API list query (`cars-query.builder.ts`) and
 * the auction's SSR first paint (`carsServerApi.ts`), so a hard refresh and a
 * client navigation can never disagree about which lots match.
 *
 * Each condition is the smart match on lots that carry `search_text`, OR —
 * on lots ingested before smart search and not yet backfilled — the caller's
 * legacy field regexes over the token AS TYPED (`token.raw`), so a deploy does
 * not have to wait for the backfill. The caller AND-s the returned conditions
 * (e.g. pushes each into `$and`); an all-stop-word query returns `[]`.
 */
export function buildSearchTokenConditions(chips, legacyConditionsForTerm) {
    return tokenizeSearchQuery(chips).map((token) => ({
        $or: [
            { $and: [{ search_text: { $exists: true } }, smartSearchCondition(token)] },
            { search_text: { $exists: false }, $or: legacyConditionsForTerm(token.raw) },
        ],
    }));
}
// ── Search document ───────────────────────────────────────────────────────
export const LOT_TYPE_LABELS_UA = {
    car: 'Легковий автомобіль',
    van: 'Фургон / мікроавтобус',
    truck: 'Вантажівка',
    motorcycle: 'Мотоцикл',
    bicycle: 'Велосипед',
    ebike: 'Електровелосипед',
    trailer: 'Причіп',
    caravan: 'Кемпер / житловий причіп',
    other: 'Інше',
};
export const FUEL_KIND_LABELS_UA = {
    petrol: 'Бензин',
    diesel: 'Дизель',
    electric: 'Електро',
    hybrid: 'Гібрид',
    mhev: 'Мʼякий гібрид',
    phev: 'Плагін-гібрид',
    lpg: 'Газ (LPG)',
    cng: 'Метан (CNG)',
    hydrogen: 'Водень',
    other: 'Інше',
};
/** Bump on any dictionary / recipe change, then backfill `search_v < N`. */
export const SEARCH_DOC_VERSION = 2;
/** Upper bound of `search_text` (cut on a word boundary). */
export const SEARCH_TEXT_MAX_LENGTH = 16_000;
/**
 * The denormalised fields `buildSearchDoc` writes on alpicar_cars. Queries
 * match on them; no response may carry them — `search_text` alone runs to
 * SEARCH_TEXT_MAX_LENGTH characters per lot. Exclude them from every read
 * whose result reaches a client.
 */
export const SEARCH_DOC_FIELDS = [
    'search_text',
    'search_tags',
    'search_v',
];
/** `{en, ua}` fields of spec §6 step 3, in document order. */
const LOCALIZED_TEXT_FIELDS = [
    'type',
    'fuel_type',
    'transmission',
    'drive',
    'color',
    'interior_color',
    'condition',
    'accident',
    'status',
    'damage_description',
    'damage_location',
    'pre_damage',
    'usable_parts',
    'defects',
    'important_information',
];
const SCALAR_IDENTITY_FIELDS = ['brand', 'make', 'model', 'year', 'vin', 'auction_name'];
const SOURCE_META_SKIPPED_KEY_PARTS = ['url', 'image', 'img', 'id', 'token', 'bidder'];
const SUMMARY_LIMITS = { maxDepth: 3, maxLength: 500 };
const SOURCE_META_LIMITS = {
    maxDepth: 2,
    maxLength: 200,
    skipKey: (key) => {
        const lower = key.toLowerCase();
        return SOURCE_META_SKIPPED_KEY_PARTS.some((part) => lower.includes(part));
    },
};
const LAST_WORD_BREAK_RE = /\s\S*$/;
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isNonBlankString(value) {
    return typeof value === 'string' && value.trim() !== '';
}
function isOneOf(values, value) {
    return typeof value === 'string' && values.includes(value);
}
/** Cut to `max` characters on the last whitespace (hard cut when there is none). */
function truncateAtWord(text, max) {
    if (text.length <= max)
        return text;
    const breakAt = text.slice(0, max + 1).search(LAST_WORD_BREAK_RE);
    return text.slice(0, breakAt > 0 ? breakAt : max);
}
/** A plain string, or the `en` and `ua` of a `{en, ua}` field. */
function localizedTexts(value) {
    if (isNonBlankString(value))
        return [value];
    if (!isRecord(value))
        return [];
    return [value.en, value.ua].filter(isNonBlankString);
}
/** `string[]` or `{en: string[], ua: string[]}`. */
function localizedListTexts(value) {
    if (Array.isArray(value))
        return value.filter(isNonBlankString);
    if (!isRecord(value))
        return [];
    return [value.en, value.ua].flatMap((list) => Array.isArray(list) ? list.filter(isNonBlankString) : []);
}
/** String leaves down to `maxDepth` (the root is depth 0), each cut to `maxLength`. */
function stringLeaves(value, limits, depth = 0) {
    if (isNonBlankString(value))
        return [truncateAtWord(value, limits.maxLength)];
    if (depth >= limits.maxDepth)
        return [];
    if (Array.isArray(value))
        return value.flatMap((item) => stringLeaves(item, limits, depth + 1));
    if (!isRecord(value))
        return [];
    return Object.entries(value)
        .filter(([key]) => !limits.skipKey?.(key))
        .flatMap(([, item]) => stringLeaves(item, limits, depth + 1));
}
/** A non-blank string, or a finite number as a string (`year: 2010 → '2010'`). */
function scalarText(value) {
    if (isNonBlankString(value))
        return [value];
    if (typeof value === 'number' && Number.isFinite(value))
        return [String(value)];
    return [];
}
function enumWithLabel(values, labels, value) {
    if (isOneOf(values, value))
        return [value, labels[value]];
    return scalarText(value);
}
/** The ordered raw text parts of spec §6 (before normalisation). `market_ua` is never read. */
function searchTextParts(car) {
    return [
        ...localizedTexts(car.name),
        ...SCALAR_IDENTITY_FIELDS.flatMap((key) => scalarText(car[key])),
        ...enumWithLabel(LOT_TYPES, LOT_TYPE_LABELS_UA, car.lot_type),
        ...enumWithLabel(ENGINE_TYPES, FUEL_KIND_LABELS_UA, car.fuel_kind),
        ...LOCALIZED_TEXT_FIELDS.flatMap((key) => localizedTexts(car[key])),
        ...scalarText(car.engine_power),
        ...scalarText(car.engine_displacement),
        ...localizedListTexts(car.special_equipment),
        ...stringLeaves(car.summary_ua, SUMMARY_LIMITS),
        ...stringLeaves(car.source_meta, SOURCE_META_LIMITS),
    ];
}
function compilePatterns(sources) {
    if (sources.length === 0)
        return null;
    return new RegExp(sources.map((source) => `(?:${source})`).join('|'), 'u');
}
/** docPatterns compiled once, per (group, doc field). */
const COMPILED_GROUPS = SEARCH_SYNONYM_GROUPS.map((g) => ({
    tag: g.tag,
    fields: CATEGORY_DOC_FIELDS[g.category].flatMap((field) => {
        const re = compilePatterns(g.docPatternsByField?.[field] ?? g.docPatterns);
        return re ? [[field, re]] : [];
    }),
}));
const TAGGED_DOC_FIELDS = [
    ...new Set(Object.values(CATEGORY_DOC_FIELDS).flat()),
];
function searchTags(car) {
    const fieldTexts = new Map(TAGGED_DOC_FIELDS.map((field) => [
        field,
        localizedTexts(car[field]).map(normalizeSearchText).filter(Boolean),
    ]));
    const enumTags = [
        ...(isOneOf(LOT_TYPES, car.lot_type) ? [`lot:${car.lot_type}`] : []),
        ...(isOneOf(ENGINE_TYPES, car.fuel_kind) ? [`fuel:${car.fuel_kind}`] : []),
    ];
    const patternTags = COMPILED_GROUPS.filter((g) => g.fields.some(([field, re]) => (fieldTexts.get(field) ?? []).some((text) => re.test(text)))).map((g) => g.tag);
    return [...new Set([...enumTags, ...patternTags])].sort();
}
/**
 * Derive `{search_text, search_tags, search_v}` from a snake_case car document
 * (the `mapPayloadToCarDoc` output or a stored `alpicar_cars` document) —
 * spec §6. Tags come only from the category fields (§5), never from long
 * free text, so a damage note mentioning "BMW" cannot tag an Audi. The one
 * exception is the anchored alpicar two-wheeler label (TWO_WHEELER_LABEL).
 */
export function buildSearchDoc(car) {
    return {
        search_text: truncateAtWord(normalizeSearchText(searchTextParts(car).join(' ')), SEARCH_TEXT_MAX_LENGTH),
        search_tags: searchTags(car),
        search_v: SEARCH_DOC_VERSION,
    };
}
//# sourceMappingURL=search.js.map